import { Button } from '@mui/material';
import {
  GridColumnMenu,
  GridColumnsManagement,
  GridPanelWrapper,
  type GridColDef,
  type GridColumnMenuProps,
  type GridColumnsPanelProps,
  type GridRowParams,
  type GridRowSelectionModel,
  type GridSortModel,
} from '@mui/x-data-grid-pro';
import { memo, useCallback, useMemo, type Dispatch, type SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ScrollableDataGrid } from '~/components/data-grid';
import { EmptyState } from '~/components/empty-state';
import { ListCheckbox } from '~/components/list-row';
import type { RowSelectionMode } from '~/hooks/data-grid';
import { useOrderByParams } from '~/hooks/order-by';
import ActivateDeviceIllustration from '~/images/illustrations/activate-device.svg';
import type { DeviceList__Device as Device } from '../../queries/list.generated';
import { useColumns, useDeviceGridState } from '../lib';
import type { DeviceCommandHandlers } from './CommandPopover';

const NoRowsOverlay = () => {
  const { t } = useTranslation();
  return (
    <EmptyState
      illustration={ActivateDeviceIllustration}
      header={t('pages.devices.empty_state.header')}
      description={t('pages.devices.empty_state.description')}
    />
  );
};

export interface TableProps {
  devices: readonly Device[];
  handleRowSelectionModeChange: (mode: RowSelectionMode) => void;
  loading: boolean;
  onCheck: Dispatch<SetStateAction<GridRowSelectionModel>>;
  actionHandlers: DeviceCommandHandlers;
  selectedIds: GridRowSelectionModel;
  rowSelectionMode: RowSelectionMode;
}

const ColumnMenu = (props: GridColumnMenuProps) => (
  <GridColumnMenu
    {...props}
    slots={{
      columnMenuSortItem: null,
      columnMenuFilterItem: null,
      columnMenuPinningItem: null,
    }}
  />
);

export const Table = memo(
  ({
    devices,
    handleRowSelectionModeChange,
    loading,
    onCheck,
    actionHandlers,
    selectedIds,
    rowSelectionMode,
  }: TableProps) => {
    const selectableCount = useMemo(
      () => devices.filter((x) => x.canUpdate.value || x.canSchedule.value).length,
      [devices],
    );

    const {
      apiRef,
      columnOrder,
      columnVisibility,
      columnWidths,
      hasPersistedPreferences,
      onColumnOrderChange,
      onColumnVisibilityChange,
      onColumnWidthChange,
      resetPreferences,
    } = useDeviceGridState();

    const columns = useColumns({
      actionHandlers,
      columnOrder,
      columnWidths,
      handleRowSelectionModeChange,
      rowSelectionMode,
      selectableCount,
    });
    const navigate = useNavigate();
    const { t } = useTranslation();

    const { setOrderByParams, sortModel } = useOrderByParams();

    const onSortModelChange = useCallback(
      (sortModel: GridSortModel) => {
        setOrderByParams(sortModel.at(0) ?? null);
      },
      [setOrderByParams],
    );

    const getTogglableColumns = useCallback(
      (cols: GridColDef[]) => cols.filter((col) => col.hideable !== false).map((col) => col.field),
      [],
    );

    const slots = useMemo(
      () => ({
        baseCheckbox: ListCheckbox,
        columnMenu: ColumnMenu,
        noRowsOverlay: NoRowsOverlay,
        columnsPanel: (props: GridColumnsPanelProps) => (
          <GridPanelWrapper {...props}>
            <GridColumnsManagement disableResetButton getTogglableColumns={getTogglableColumns} />
            <Button
              disabled={!hasPersistedPreferences.current}
              onClick={resetPreferences}
              size="small"
              sx={{ alignSelf: 'flex-end', m: 0.5, mt: '-35px' }}
            >
              {t('common.buttons.reset')}
            </Button>
          </GridPanelWrapper>
        ),
      }),
      [getTogglableColumns, resetPreferences, hasPersistedPreferences, t],
    );

    return (
      <ScrollableDataGrid
        apiRef={apiRef}
        // recommended by MUI even with custom header
        // https://mui.com/x/react-data-grid/row-selection/
        checkboxSelection
        columns={columns}
        columnVisibilityModel={columnVisibility}
        disableColumnMenu={false}
        disableColumnReorder={false}
        disableRowSelectionOnClick
        enableHorizontalScrolling
        loading={loading}
        isRowSelectable={({ row }) => row.canUpdate.value || row.canSchedule.value}
        onColumnOrderChange={onColumnOrderChange}
        onColumnVisibilityModelChange={onColumnVisibilityChange}
        onColumnWidthChange={onColumnWidthChange}
        onRowSelectionModelChange={onCheck}
        onRowClick={({ row }: GridRowParams<Device>) => navigate(`${row.id}/details`)}
        pinnedColumns={{ right: ['actions'] }}
        rows={devices}
        rowSelectionModel={selectedIds}
        slots={slots}
        sortingMode="server"
        sortModel={sortModel}
        onSortModelChange={onSortModelChange}
      />
    );
  },
);

Table.displayName = 'Table';
